import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid, 
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import InvoiceModel, {
  InvoiceCreateModel,
} from "../../core/api/invoice/InvoiceModel";
import InvoiceModalView from "./InvoiceModalView";
import ClientModel from "../../core/api/clients/ClientModel";
import { useNotifications } from "../../hooks/NotificationProvider";
import { DatePicker } from "../common/DatePicker";
import { StyledTextField } from "./InvoiceStyles";
import { translations } from "../../translations/common";

interface Props {
  clientId: string;
  close(): void;
  onSuccess(id: string): void;
  tableRef: React.RefObject<any>;
}

interface PreviewableInvoice extends InvoiceCreateModel {
  preview?: boolean;
}

interface PreviewState {
  preview: boolean;
  pdfUrl?(): Promise<string>;
}

const InvoiceModalForm: React.FC<Props> = (props) => {
  const closePopup = props.close;
  const notifications = useNotifications();
  const [form, setForm] = useState<PreviewableInvoice>(
    InvoiceCreateModel.defaultValue(props.clientId)
  );

  useEffect(() => {
    ClientModel.findOne(props.clientId).then((client) => {
      if (client.validFrom && client.validTo) {
        setForm(
          InvoiceCreateModel.withDates(
            props.clientId,
            client.validFrom,
            client.validTo
          )
        );
      }
    });
  }, [props.clientId]);
  const [preview, setPreview] = useState<PreviewState>({ preview: false });
  return (
    <div>
      <Dialog
        open={true}
        onClose={closePopup}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
      >
        <DialogTitle id="form-dialog-title">{translations.invoice.form.title}</DialogTitle>
        <Formik
          initialValues={form}
          enableReinitialize={true}
          validationSchema={InvoiceCreateModel.createSchema}
          onSubmit={(values, actions) => {
            if (values.preview) {
              setForm(values);
              setPreview({
                preview: true,
                pdfUrl: () => InvoiceCreateModel.pdfPreview(values),
              });
              actions.setSubmitting(false);
            } else {
              InvoiceModel.create(values)
                .then((body) => {
                  closePopup();
                  notifications.notify({
                    level: "success",
                    message: translations.invoice.form.success,
                  });
                  props.onSuccess(body.id);
                  
                  if (props.tableRef && props.tableRef.current && typeof props.tableRef.current.onQueryChange === 'function') {
                    props.tableRef.current.onQueryChange();
                  }
                })
                .catch((error) => {
                  notifications.notify({
                    level: "error",
                    message: error.response?.data?.description || "Failed to create invoice",
                  });
                  
                  if (error.response?.data?.errors) {
                    error.response.data.errors.forEach((element: any) => {
                      actions.setFieldError(
                        element.field,
                        element.defaultMessage
                      );
                    });
                  }
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }
          }}
        >
          {(props: FormikProps<PreviewableInvoice>) => {
            const {
              values,
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              setFieldValue,
            } = props;
            return (
              <Form>
                <DialogContent>
                  <Grid
                    container
                    justifyContent="space-around"
                    direction="row"
                    spacing={2}
                  >
                    <Grid size={12}>
                      <DatePicker
                        id="date"
                        name="date"
                        label={translations.invoice.form.fields.date}
                        value={values.date}
                        onChange={handleChange}
                        error={!!errors.date}
                        helperText={errors.date}
                      />
                    </Grid>
                    <Grid size={12}>
                      <DatePicker
                        id="periodFrom"
                        name="periodFrom"
                        label={translations.invoice.form.fields.periodFrom}
                        value={values.periodFrom}
                        onChange={(e) => {
                          const newPeriodFrom = e.target.value;
                          handleChange(e);
                          // Update productPeriod when periodFrom changes
                          if (newPeriodFrom && values.periodTo) {
                            setFieldValue("productPeriod", `${newPeriodFrom} - ${values.periodTo}`);
                          }
                        }}
                        error={!!errors.periodFrom}
                        helperText={errors.periodFrom}
                      />
                    </Grid>
                    <Grid size={12}>
                      <DatePicker
                        id="periodTo"
                        name="periodTo"
                        label={translations.invoice.form.fields.periodTo}
                        value={values.periodTo}
                        onChange={(e) => {
                          const newPeriodTo = e.target.value;
                          handleChange(e);
                          // Update productPeriod when periodTo changes
                          if (values.periodFrom && newPeriodTo) {
                            setFieldValue("productPeriod", `${values.periodFrom} - ${newPeriodTo}`);
                          }
                        }}
                        error={!!errors.periodTo}
                        helperText={errors.periodTo}
                      />
                    </Grid>
                    <Grid size={12}>
                      <NumericFormat
                        customInput={StyledTextField}
                        name="price"
                        id="price"
                        label={translations.invoice.form.fields.price}
                        value={values.price ?? ""}
                        fullWidth
                        allowedDecimalSeparators={[".", ","]}
                        helperText={errors.price}
                        error={Boolean(errors.price)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid size={12}>
                      <NumericFormat
                        customInput={StyledTextField}
                        name="discount"
                        id="discount"
                        label={translations.invoice.form.fields.discount}
                        value={values.discount ?? ""}
                        fullWidth
                        allowedDecimalSeparators={[".", ","]}
                        helperText={errors.discount}
                        error={Boolean(errors.discount)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <StyledTextField
                        name="jobTitle"
                        id="jobTitle"
                        label={translations.invoice.form.fields.jobTitle}
                        value={values.jobTitle}
                        type="text"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <StyledTextField
                        name="product"
                        id="product"
                        label={translations.invoice.form.fields.product}
                        value={values.product}
                        type="text"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <StyledTextField
                        name="productPeriod"
                        id="productPeriod"
                        label={translations.invoice.form.fields.productPeriod}
                        value={values.productPeriod}
                        type="text"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => {
                      setFieldValue("preview", true);
                    }}
                  >
                    {translations.invoice.form.buttons.preview}
                  </Button>
                  <Button
                    type="button"
                    className="outline"
                    onClick={closePopup}
                    disabled={isSubmitting}
                  >
                    {translations.invoice.form.buttons.cancel}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => setFieldValue("preview", false)}
                    variant="contained"
                    color="primary"
                  >
                    {translations.invoice.form.buttons.save}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
      {preview && preview.pdfUrl && (
        <InvoiceModalView
          close={() => setPreview({ preview: false })}
          pdfUrl={preview.pdfUrl}
        />
      )}
    </div>
  );
};

export default InvoiceModalForm;
